import React from "react";
import { Flex, Text } from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import CustomIcon from "../../../../../../../public/icons-v1_1";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useMembership from "@/shared/hooks/useMembership";
import PathPickBasketActionCollapsibleRow from "@/features/pathpick/features/basket/components/action-panel/PathPickBasketActionCollapsibleRow";
import useSyncRequestFlow from "@/features/pathpick/features/basket/hooks/useSyncRequestFlow";
import usePathPickBasketSelection from "@/features/pathpick/features/basket/hooks/usePathPickBasketSelection";
import PathPickBasketFileRequestModal from "@/features/pathpick/features/basket/components/sync/PathPickBasketFileRequestModal";
import PathPickBasketSyncConfirmModal from "@/features/pathpick/features/basket/components/sync/PathPickBasketSyncConfirmModal";
import PathPickBasketSyncCompleteModal from "@/features/pathpick/features/basket/components/sync/PathPickBasketSyncCompleteModal";
import PathPickBasketAlreadyRequestedModal from "@/features/pathpick/features/basket/components/sync/PathPickBasketAlreadyRequestedModal";
import PathPickBasketDownloadCompleteModal from "@/features/pathpick/features/basket/components/sync/PathPickBasketDownloadCompleteModal";
import PathPickBasketBuyerInfoModal from "@/features/pathpick/features/basket/components/buyer-info/PathPickBasketBuyerInfoModal";

// 상품 연동/다운로드 행 + 관련 플로우 모달 소유
function PathPickBasketProductSyncPanel() {
  const { t } = useI18nHelper();
  // API 연동 권한이 있는 플랜(BASIC 포함)은 API/파일 신청을 각각, 미허용 플랜은 파일 신청만 제공한다.
  const { isApiSync, canDownload } = useMembership();
  const { selectedProducts, selectedCount, soldOutCount } =
    usePathPickBasketSelection();
  const {
    step,
    intent,
    blockedStep,
    isRequesting,
    smartStoreInfo,
    syncKinds,
    isNaverReady,
    startApiSync,
    startFileRequest,
    submitFileRequest,
    openBuyerInfo,
    handleBuyerInfoSaved,
    confirmSync,
    dismiss,
  } = useSyncRequestFlow();

  const hasSelection = selectedCount > 0;
  // 품절 상품이 섞여 있으면 선택을 해제하기 전까지 신청 자체를 막는다.
  const hasSoldOut = soldOutCount > 0;
  const isApiDisabled = !hasSelection || !isApiSync || hasSoldOut;
  const isDownloadDisabled = !hasSelection || !canDownload || hasSoldOut;

  return (
    <>
      <PathPickBasketActionCollapsibleRow
        icon={isApiSync ? "upload-cloud" : "download"}
        title={t(
          isApiSync
            ? "path_pick:basket.action_panel.sync_title"
            : "path_pick:basket.action_panel.download_title"
        )}
        description={
          <>
            <Text textStyle={"body-sm-medium"} color={"#8F8F8C"}>
              {t(
                isApiSync
                  ? "path_pick:basket.action_panel.sync_description"
                  : "path_pick:basket.action_panel.download_description"
              )}
            </Text>
            <Flex marginTop={"6px"}>
              <AppButton
                variant={"white"}
                height={"32px"}
                onClick={openBuyerInfo}
                chakraProps={{ paddingInline: "16px" }}
              >
                <Text textStyle={"body-sm-bold"}>
                  {t("path_pick:basket.buyer_info.set_button")}
                </Text>
              </AppButton>
            </Flex>
          </>
        }
        actions={
          <Flex
            flexDirection={"column"}
            gap={"8px"}
            width={{ base: "100%", md: "184px" }}
            flexShrink={0}
          >
            {isApiSync && (
              <AppButton
                variant={"primary"}
                width={"100%"}
                height={"36px"}
                isDisabled={isApiDisabled}
                onClick={startApiSync}
              >
                <Flex
                  gap={"4px"}
                  alignItems={"center"}
                  justifyContent={"center"}
                >
                  <CustomIcon
                    src={"upload-cloud"}
                    width={16}
                    height={16}
                    color={isApiDisabled ? "#8F8F8C" : "#FFFFFF"}
                  />
                  <Text textStyle={"body-sm-bold"}>
                    {t("path_pick:basket.download_request.submit_api")}
                  </Text>
                </Flex>
              </AppButton>
            )}
            <AppButton
              variant={"primary"}
              width={"100%"}
              height={"36px"}
              isDisabled={isDownloadDisabled}
              onClick={startFileRequest}
            >
              <Flex gap={"4px"} alignItems={"center"} justifyContent={"center"}>
                <CustomIcon
                  src={"download"}
                  width={16}
                  height={16}
                  color={isDownloadDisabled ? "#8F8F8C" : "#FFFFFF"}
                />
                <Text textStyle={"body-sm-bold"}>
                  {t("path_pick:basket.action_panel.file_request_button")}
                </Text>
              </Flex>
            </AppButton>
          </Flex>
        }
      />

      {/* 연동/파일 신청 플로우 모달 — 단계별 단일 렌더.
          닫기는 모두 dismiss(한 단계 뒤로)다. 파일 신청 관문만 스택을 쌓으므로
          기본 정보 모달에서 취소하면 양식 선택으로 돌아가고, 그 외에는 플로우가 닫힌다. */}
      {step?.name === "fileFormatSelect" && (
        <PathPickBasketFileRequestModal
          isOpen
          onClose={dismiss}
          selectedProducts={selectedProducts}
          smartStoreInfo={smartStoreInfo}
          // 관문을 거쳐 돌아왔을 때 고른 양식을 잃지 않도록 확정된 의도에서 복원한다.
          initialFormats={
            intent?.kind === "fileRequest" ? intent.formats : undefined
          }
          isLoading={isRequesting}
          onConfirm={submitFileRequest}
        />
      )}
      {step?.name === "buyerInfo" && (
        <PathPickBasketBuyerInfoModal
          isOpen
          onClose={dismiss}
          variant={step.variant}
          isBlocked={blockedStep === "buyerInfo"}
          onSaved={handleBuyerInfoSaved}
          isResumingRequest={isRequesting}
        />
      )}
      {step?.name === "syncConfirm" && (
        <PathPickBasketSyncConfirmModal
          isOpen
          onClose={dismiss}
          selectedProducts={selectedProducts}
          // 네이버 외 마켓 권한이 있는 바이어는 권한 있는 마켓 중 골라 신청한다.
          syncKinds={syncKinds}
          isNaverReady={isNaverReady}
          // 네이버 기본 정보 모달에서 취소하고 돌아오면 앞서 고른 마켓을 그대로 보여 준다.
          initialKinds={intent?.kind === "apiSync" ? intent.markets : undefined}
          isLoading={isRequesting}
          onConfirm={confirmSync}
        />
      )}
      {step?.name === "syncComplete" && (
        <PathPickBasketSyncCompleteModal isOpen onClose={dismiss} />
      )}
      {step?.name === "alreadyRequested" && (
        <PathPickBasketAlreadyRequestedModal isOpen onClose={dismiss} />
      )}
      {step?.name === "downloadComplete" && (
        <PathPickBasketDownloadCompleteModal
          isOpen
          onClose={dismiss}
          smartStoreInfo={smartStoreInfo}
        />
      )}
    </>
  );
}

export default PathPickBasketProductSyncPanel;
